/* ================================================================
   LF WEB CONNECTORS — Design Checkpoint 2 (B9 revision)
   ----------------------------------------------------------------
   REPLACES the Checkpoint 1 ambient floating-node canvas
   (lf-web-bg.js/css — now retired, kept in the repo history only).
   Jaydo's note this checkpoint: "make sure the spider webs arent an
   actual background... i mostly just want them connecting to
   different things... i want different things connected everywhere
   so it just makes it look like that page is just fully built for
   that."

   So: no more floating nodes with no relationship to the page. This
   engine finds REAL elements already marked `[data-lf-web-node]` in
   a page's actual markup (nav items, footer signature, room content
   — never fabricated placeholder dots) and draws thin lines
   connecting each one to its K nearest neighbors, anchored to each
   element's real screen position. The web moves WITH the page
   (scroll/resize), because it's tracing real content, not floating
   in empty space independent of it.

   OPT-IN, PER ELEMENT: only elements explicitly marked
   `data-lf-web-node` participate. This is a deliberate content
   decision made where the attribute is added (see header.liquid /
   footer.liquid this checkpoint), not something this script infers
   automatically — inferring it would risk wiring lines to content
   that hasn't shipped yet or doesn't make sense connected.

   Skips itself when:
   - data-room="lfj" (LFJ room keeps its own dedicated drag-canvas web)
   - prefers-reduced-motion
   - canvas API unavailable
   - fewer than 2 marked nodes exist on the page (nothing to connect)

   PERFORMANCE:
   - Anchor positions (getBoundingClientRect) re-measured on scroll/
     resize, rAF-throttled — not every frame, since real DOM elements
     don't move on their own.
   - K-NN connection list is only rebuilt when positions actually
     change (scroll/resize), not per animation frame.
   - A very light per-frame sinusoidal wobble on each line's midpoint
     keeps the web from reading as static/flat, without moving the
     real anchor points themselves.
================================================================ */
(function () {
  'use strict';

  if (window.__lfWebConnectorsBound) return;
  window.__lfWebConnectorsBound = true;

  var body = document.body;
  if (body && body.getAttribute('data-room') === 'lfj') return;

  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  } catch (e) {}

  var CONNECT_K   = 2;     /* nearest-neighbor connections per node */
  var LINE_ALPHA  = 0.22;
  var NODE_RADIUS = 2;
  var WOBBLE_AMP  = 3;     /* px, midpoint wobble */
  var PALETTE = ['#FF2D78', '#00FFD1', '#FFE600', '#BF00FF', '#39FF14'];
  var paletteStep = 0;

  function init() {
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-lf-web-node]'));
    if (els.length < 2) return; /* nothing real to connect — no fallback ambient nodes */

    var canvas = document.createElement('canvas');
    canvas.id = 'lf-web-connectors-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);
    var ctx = canvas.getContext('2d');
    if (!ctx) { canvas.remove(); return; }

    var W, H, nodes, pairs;

    function resize() {
      W = canvas.width  = window.innerWidth;
      H = canvas.height = window.innerHeight;
      measure();
    }

    function measure() {
      nodes = els.map(function (el) {
        var r = el.getBoundingClientRect();
        return {
          x: r.left + r.width / 2,
          y: r.top + r.height / 2,
          visible: r.bottom > 0 && r.top < H && r.right > 0 && r.left < W
        };
      });
      pairs = buildConnections(nodes);
    }

    function buildConnections(list) {
      var out = [];
      for (var a = 0; a < list.length; a++) {
        if (!list[a].visible) continue;
        var dists = [];
        for (var b = 0; b < list.length; b++) {
          if (a === b || !list[b].visible) continue;
          var dx = list[b].x - list[a].x, dy = list[b].y - list[a].y;
          dists.push({ b: b, d: Math.sqrt(dx * dx + dy * dy) });
        }
        dists.sort(function (x, y) { return x.d - y.d; });
        for (var k = 0; k < Math.min(CONNECT_K, dists.length); k++) {
          var nb = dists[k].b;
          if (nb > a) out.push([a, nb]);
        }
      }
      return out;
    }

    function getLineColor() {
      var mode = document.documentElement.getAttribute('data-color-mode');
      if (mode === 'bw') return 'rgba(255,255,255,' + LINE_ALPHA + ')';
      if (mode === 'lfj') {
        var hex = PALETTE[paletteStep % PALETTE.length];
        var r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
        return 'rgba(' + r + ',' + g + ',' + b + ',' + (LINE_ALPHA + 0.1) + ')';
      }
      return 'rgba(139,143,120,' + LINE_ALPHA + ')';
    }
    function getNodeColor() {
      var mode = document.documentElement.getAttribute('data-color-mode');
      if (mode === 'bw') return 'rgba(255,255,255,0.4)';
      if (mode === 'lfj') return PALETTE[paletteStep % PALETTE.length];
      return 'rgba(139,143,120,0.4)';
    }

    var frameCount = 0;
    function draw() {
      ctx.clearRect(0, 0, W, H);
      if (!pairs || !pairs.length) return;

      frameCount++;
      if (frameCount % 30 === 0) paletteStep = (paletteStep + 1) % PALETTE.length;

      var lineColor = getLineColor();
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 0.8;
      pairs.forEach(function (p) {
        var a = nodes[p[0]], b = nodes[p[1]];
        var mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
        var wob = Math.sin((frameCount / 40) + (p[0] + p[1])) * WOBBLE_AMP;
        var dx = b.y - a.y, dy = a.x - b.x; /* perpendicular unit-ish */
        var len = Math.sqrt(dx * dx + dy * dy) || 1;
        mx += (dx / len) * wob;
        my += (dy / len) * wob;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.quadraticCurveTo(mx, my, b.x, b.y);
        ctx.stroke();
      });

      ctx.fillStyle = getNodeColor();
      nodes.forEach(function (n) {
        if (!n.visible) return;
        ctx.beginPath();
        ctx.arc(n.x, n.y, NODE_RADIUS, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    var running = true;
    function loop() {
      if (!running) return;
      draw();
      requestAnimationFrame(loop);
    }

    var measureQueued = false;
    function queueMeasure() {
      if (measureQueued) return;
      measureQueued = true;
      requestAnimationFrame(function () { measure(); measureQueued = false; });
    }

    resize();
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('scroll', queueMeasure, { passive: true });

    document.addEventListener('visibilitychange', function () {
      running = !document.hidden;
      if (running) loop();
    });

    requestAnimationFrame(function () {
      draw();
      canvas.classList.add('is-ready');
      loop();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
